import {
    BaseScoreResult,
    QuestionnaireResponse,
    getAnswerCode,
    getAnswerNumber,
    findItemByLinkId
} from './types';


export interface PittsburghComponent {
    name: string;
    score: number;
    maxScore: number;
}

export interface PittsburghScoreResult extends BaseScoreResult {
    totalScore: number;
    sleepQuality: 'good' | 'poor';
    components: PittsburghComponent[];
    sleepEfficiency: number;
    sleepDuration: number;
    sleepLatency: number;
    details: Record<string, number>;
}

export function processPittsburghQuestionnaire(response: QuestionnaireResponse): PittsburghScoreResult {
    const rootItems = response.item || [];

    const bedtime = getAnswerCode(findItemByLinkId(rootItems, 'bedtime'));
    const wakeTime = getAnswerCode(findItemByLinkId(rootItems, 'wake-time'));
    const sleepDuration = getAnswerNumber(findItemByLinkId(rootItems, 'sleep-duration-hours')) || 0;
    const sleepLatency = getAnswerNumber(findItemByLinkId(rootItems, 'sleep-latency-minutes')) || 0;

    const sleepQualityCode = getAnswerCode(findItemByLinkId(rootItems, 'sleep-quality'));
    let component1Score = 0;
    if (sleepQualityCode) {
        component1Score = parseInt(sleepQualityCode, 10) - 1;
    }

    let latencyScore = 0;
    if (sleepLatency <= 15) latencyScore = 0;
    else if (sleepLatency <= 30) latencyScore = 1;
    else if (sleepLatency <= 60) latencyScore = 2;
    else latencyScore = 3;

    const difficultyFallingAsleepCode = getAnswerCode(findItemByLinkId(rootItems, 'disturbance-difficulty-falling-asleep'));
    let difficultyScore = 0;
    if (difficultyFallingAsleepCode) {
        difficultyScore = parseInt(difficultyFallingAsleepCode, 10) - 1;
    }

    const component2Total = latencyScore + difficultyScore;
    let component2Score = 0;
    if (component2Total === 0) component2Score = 0;
    else if (component2Total <= 2) component2Score = 1;
    else if (component2Total <= 4) component2Score = 2;
    else component2Score = 3;

    let component3Score = 0;
    if (sleepDuration > 7) component3Score = 0;
    else if (sleepDuration >= 6) component3Score = 1;
    else if (sleepDuration >= 5) component3Score = 2;
    else component3Score = 3;

    let sleepEfficiency = 0;
    if (bedtime && wakeTime) {
        const bedtimeHour = parseInt(bedtime.split(':')[0], 10);
        const wakeHour = parseInt(wakeTime.split(':')[0], 10);
        let bedTimeHours = wakeHour - bedtimeHour;
        if (bedTimeHours < 0) bedTimeHours += 24;

        if (bedTimeHours > 0) {
            sleepEfficiency = (sleepDuration / bedTimeHours) * 100;
        }
    }

    let component4Score = 0;
    if (sleepEfficiency > 85) component4Score = 0;
    else if (sleepEfficiency >= 75) component4Score = 1;
    else if (sleepEfficiency >= 65) component4Score = 2;
    else component4Score = 3;

    const disturbanceQuestions = [
        'disturbance-awakenings',
        'disturbance-bathroom',
        'disturbance-breathing',
        'disturbance-cough-snore',
        'disturbance-cold',
        'disturbance-heat',
        'disturbance-nightmares',
        'disturbance-pain'
    ];

    let disturbanceTotal = 0;
    disturbanceQuestions.forEach(linkId => {
        const code = getAnswerCode(findItemByLinkId(rootItems, linkId));
        if (code) {
            disturbanceTotal += parseInt(code, 10) - 1;
        }
    });

    let component5Score = 0;
    if (disturbanceTotal === 0) component5Score = 0;
    else if (disturbanceTotal <= 9) component5Score = 1;
    else if (disturbanceTotal <= 18) component5Score = 2;
    else component5Score = 3;

    const medicationCode = getAnswerCode(findItemByLinkId(rootItems, 'sleep-medication-frequency'));
    let component6Score = 0;
    if (medicationCode) {
        component6Score = parseInt(medicationCode, 10) - 1;
    }

    const sleepinessCode = getAnswerCode(findItemByLinkId(rootItems, 'sleepiness-during-activities'));
    const enthusiasmCode = getAnswerCode(findItemByLinkId(rootItems, 'enthusiasm-difficulty'));

    let sleepinessScore = 0;
    let enthusiasmScore = 0;

    if (sleepinessCode) {
        sleepinessScore = parseInt(sleepinessCode, 10) - 1;
    }

    if (enthusiasmCode) {
        enthusiasmScore = parseInt(enthusiasmCode, 10) - 1;
    }

    const component7Total = sleepinessScore + enthusiasmScore;
    let component7Score = 0;
    if (component7Total === 0) component7Score = 0;
    else if (component7Total <= 2) component7Score = 1;
    else if (component7Total <= 4) component7Score = 2;
    else component7Score = 3;

    const totalScore = component1Score + component2Score + component3Score +
        component4Score + component5Score + component6Score + component7Score;

    const sleepQuality = totalScore < 5 ? 'good' : 'poor';

    const details: Record<string, number> = {
        'component1': component1Score,
        'component2': component2Score,
        'component3': component3Score,
        'component4': component4Score,
        'component5': component5Score,
        'component6': component6Score,
        'component7': component7Score,
        'sleepEfficiency': sleepEfficiency,
        'sleepDuration': sleepDuration,
        'sleepLatency': sleepLatency
    };

    const components: PittsburghComponent[] = [
        { name: 'Qualidade subjetiva do sono', score: component1Score, maxScore: 3 },
        { name: 'Latência do sono', score: component2Score, maxScore: 3 },
        { name: 'Duração do sono', score: component3Score, maxScore: 3 },
        { name: 'Eficiência do sono', score: component4Score, maxScore: 3 },
        { name: 'Distúrbios do sono', score: component5Score, maxScore: 3 },
        { name: 'Uso de medicação', score: component6Score, maxScore: 3 },
        { name: 'Sonolência diurna', score: component7Score, maxScore: 3 }
    ];

    return {
        questionnaireId: response.questionnaire?.split('/')?.[1],
        questionnaireResponseId: response.id,
        totalScore,
        sleepQuality,
        components,
        sleepEfficiency,
        sleepDuration,
        sleepLatency,
        details
    };
}
